/**
 * @module Documents
 */

import { Dialog } from '@mui/material'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import { createContext, useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import ThemeManager from 'theming/ThemeManager'
import BloqueantesListModal from '../components/BloqueantesListModal'
import LockDocumentNotification from '../components/LockDocumentNotification'
import useFetchLaboral from '../hooks/useFetchLaboral'
import DocumentsController from '../libs/CorporateController'
/**
 * @typedef DocumentsNotificationContext
 * @prop {function} forceRefresh - Force the refresh of the notifications
 * @prop {import('common/types/connections_types').PaginatedResponse<LaboralDocDTO>} response - The request full response
 * @prop {boolean} loading - Is currently loading the request
 * @prop {AxiosErr} error - The error of the request
 * @prop {number} count - The total ammount of unread documents
 * @prop {function} launchModal - Launch The last Locking document modal
 * @example
 * const { count, response, loading, errror, forceRefresh} ) = useContext(NotificationsContext)
 */

/**
 * @type {import('react').Context<DocumentsNotificationContext>}
 */
let DocumentNotificationContext

/**
 * @type {import('react').Context<DocumentsNotificationContext>}
 */
const { Provider } = (DocumentNotificationContext = createContext())

const DocumentNotificationProvider = ({ children }) => {
  const themeController = new ThemeManager()
  const { handleModal, modal } = useContext(CustomModalContext)
  const [modalContent, setModalContent] = useState(null)
  const [iteration, setIteration] = useState(0)
  const { response, loading, error } = useFetchLaboral({
    unforce_year: true,
    iteration: iteration,
    tipo_documento: themeController?.options?.notifications?.laboral_document_type || null,
    only_unread: true,
    paginate: true,
  })

  function forceRefresh() {
    setIteration(iteration + 1)
  }

  function hideModal() {
    setModalContent(null)
  }

  function launchModalWithList(bloqueantes) {
    if (!bloqueantes || bloqueantes.length === 0) return
    setModalContent(<BloqueantesListModal bloqueantes={bloqueantes} onClose={hideModal} />)
  }

  async function launchModal() {
    const Theme = new ThemeManager()
    const NotificationComponent = Theme.components?.LockDocumentNotification
    try {
      const data = await DocumentsController.getLastUnreaded()

      if (data) {
        setModalContent(
          <Dialog open={true} maxWidth="sm" fullWidth={true} onClose={() => {}}>
            {NotificationComponent ? (
              <NotificationComponent data={data} />
            ) : (
              <LockDocumentNotification data={data} />
            )}
          </Dialog>,
        )
      }
    } catch (error) {
      setModalContent(null)
    }
  }

  return (
    <Provider
      value={{
        launchModal,
        launchModalWithList,
        forceRefresh,
        hideModal,
        response,
        loading,
        error,
        count: response?.count,
      }}
    >
      {children}
      {modalContent && createPortal(modalContent, document.querySelector('#lockmodal-root'))}
    </Provider>
  )
}

export { DocumentNotificationContext, DocumentNotificationProvider }
